import { faArrowUpFromBracket, faMobileNotch, faPlus } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@/design-system/primitives/Button';
import { IconDisc } from '@/design-system/primitives/IconDisc';
import { Modal } from '@/design-system/primitives/Modal';

export interface InstallAppModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Numbered step in the iOS install flow. Number bubble + body text +
 *  optional trailing icon (Share + Plus) so the page mirrors what the
 *  user sees in Safari. */
const Step = ({ num, children }: { num: number; children: React.ReactNode }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-secondary-emphasized text-white font-semibold text-xs flex items-center justify-center leading-none">
      {num}
    </span>
    <span className="text-sm text-content-text leading-snug pt-0.5">{children}</span>
  </li>
);

/** iOS-only "Add to Home Screen" instructions modal. iOS Safari doesn't
 *  expose a programmatic install API, so the only path is the user
 *  manually triggering Share → Add to Home Screen. Non-iOS browsers
 *  fall through to the native install prompt (see useInstallPrompt) and
 *  this modal is never shown. */
export const InstallAppModal = ({ isOpen, onClose }: InstallAppModalProps) => (
  <Modal isOpen={isOpen} onClose={onClose}>
    <Modal.Body>
      <div className="flex justify-center mb-4">
        <IconDisc icon={faMobileNotch} size="lg" tone="primary" />
      </div>
      <h2 className="font-diary text-2xl text-content-text text-center mb-2">Add to Home Screen</h2>
      <p className="text-sm text-content-subtle leading-relaxed text-center mb-4">
        Install Trippy Planny on your iPhone or iPad — opens full-screen, no Safari chrome, and
        shows up alongside your other apps.
      </p>
      <ol className="space-y-3">
        <Step num={1}>
          Tap the{' '}
          <strong className="text-content-text">
            Share{' '}
            <FontAwesomeIcon
              icon={faArrowUpFromBracket}
              className="text-secondary-emphasized"
              style={{ width: 12, height: 12, marginInline: 2 }}
            />
          </strong>{' '}
          button in Safari's toolbar.
        </Step>
        <Step num={2}>
          Scroll down and tap{' '}
          <strong className="text-content-text">
            Add to Home Screen{' '}
            <FontAwesomeIcon
              icon={faPlus}
              className="text-secondary-emphasized"
              style={{ width: 11, height: 11, marginInline: 2 }}
            />
          </strong>
          .
        </Step>
        <Step num={3}>
          Tap <strong className="text-content-text">Add</strong> in the top-right corner — Trippy
          Planny will appear on your home screen. ✨
        </Step>
      </ol>
    </Modal.Body>
    <Modal.Footer>
      <Button onClick={onClose}>Got it</Button>
    </Modal.Footer>
  </Modal>
);
